import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { BrandingLanguage } from '../../../../i18n'

/** Branding config area holding the CX Suite partner-branding settings (CXUI-1002). */
export const CX_SUITE_BRANDING_KEY = 'cxSuite' as const

/** The CX Suite branding area as sent to CCS. The new-UI SSO buttons join it in APD-13979. */
export type CxSuiteBrandingBackend = {
    url: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Normalises the CX Suite branding area of the CME form before it is sent to CCS: trims the URL. CCS performs the
 * authoritative validation (hostname rules and uniqueness across CMEs).
 *
 * @param value - The `branding.cxSuite` form value; anything that is not an object is treated as empty.
 *
 * @returns The area to store in the CME branding config.
 */
export const mapCxSuiteBranding = (value: unknown): CxSuiteBrandingBackend => {
    const url: unknown = isRecord(value) ? value.url : undefined

    return { url: typeof url === 'string' ? url.trim() : EMPTY_STRING }
}

/**
 * Picks the admin message for a CX Suite publish warning from the CME save response (CXUI-1002). The
 * value is CCS's publish outcome; anything unknown gets the retrying message, the mildest one.
 *
 * @param warning - The `CxSuiteBrandingWarning` value: `RETRYING`, `FAILED` or `CONFLICT`.
 *
 * @returns The `BrandingLanguage` key of the message to show.
 */
export const cxSuitePublishWarningKey = (warning: Definable<string>): string => {
    switch (warning) {
        case 'FAILED':
            return BrandingLanguage.Branding_CxSuite_PublishFailed
        case 'CONFLICT':
            return BrandingLanguage.Branding_CxSuite_PublishConflict
        default:
            return BrandingLanguage.Branding_CxSuite_PublishWarning
    }
}
